import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Pagination,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import {
  Add, Check, Close, Calculate, LocalGasStation, Refresh,
  HourglassEmpty, CheckCircle, Cancel, Receipt,
} from '@mui/icons-material';
import FrotaPageLayout, { FrotaStatItem } from '../../components/frota/FrotaPageLayout';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import UsuariosSelect from '../../components/selects/UsuariosSelect';
import { toast } from 'react-toastify';
import frotaService from '../../services/frotaService';
import { RequisicaoAbastecimento, TIPOS_COMBUSTIVEL, TipoCombustivel, Veiculo } from '../../types/frota';
import VeiculoSelect from '../../components/selects/VeiculoSelect';
import FuncionariosSelect from '../../components/selects/FuncionariosSelect';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import {
  formatarValorOnInput, parsearValorFormatado,
} from '../../utils/formatToView';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pendente: 'warning', aprovada: 'success', rejeitada: 'error', apurada: 'info', paga: 'success',
};

const STATUS_COLORS = {
  pendente: '#FF9800',
  aprovada: '#4CAF50',
  rejeitada: '#F44336',
  apurada: '#2196F3',
} as const;

const dateToYmd = (date: Date | null) => {
  if (!date) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const AbastecimentosPage: React.FC = () => {
  const [items, setItems] = useState<RequisicaoAbastecimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [dataRange, setDataRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [filtroVeiculoId, setFiltroVeiculoId] = useState<number | null>(null);
  const [filtroSolicitanteId, setFiltroSolicitanteId] = useState<number | null>(null);
  const [filtroUsuarioLancouId, setFiltroUsuarioLancouId] = useState<number | null>(null);
  const [filtroCombustivel, setFiltroCombustivel] = useState<TipoCombustivel | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, pendente: 0, aprovada: 0, rejeitada: 0, apurada: 0 });
  const [openForm, setOpenForm] = useState(false);
  const [openApuracao, setOpenApuracao] = useState(false);
  const [resumo, setResumo] = useState<any>(null);
  const [form, setForm] = useState({
    veiculo_id: null as number | null, kilometragem_atual: 0, valor: 0,
    tipo_combustivel: 'gasolina' as TipoCombustivel, valor_litro: 0,
    funcionario_solicitante_id: null as number | null, data_requisicao: new Date().toISOString().split('T')[0],
    observacoes: '', escritorio_id: null as number | null,
  });
  const [apuracao, setApuracao] = useState({
    mes_referencia: '', escritorio_id: null as number | null, data_vencimento: '', observacoes: '',
  });
  const [valorFormatado, setValorFormatado] = useState('0,00');
  const [valorLitroFormatado, setValorLitroFormatado] = useState('0,00');
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<Veiculo | null>(null);

  const resetFormValores = () => {
    setValorFormatado('0,00');
    setValorLitroFormatado('0,00');
    setVeiculoSelecionado(null);
    setForm({
      veiculo_id: null, kilometragem_atual: 0, valor: 0,
      tipo_combustivel: 'gasolina' as TipoCombustivel, valor_litro: 0,
      funcionario_solicitante_id: null, data_requisicao: new Date().toISOString().split('T')[0],
      observacoes: '', escritorio_id: null,
    });
  };

  const quantidadeLitros = frotaService.calcularQuantidadeLitros(form.valor, form.valor_litro);
  const estatisticas = frotaService.calcularEstatisticasAbastecimento({
    kilometragem_atual: form.kilometragem_atual,
    quantidade_litros: quantidadeLitros,
    media_consumo_km_litro: veiculoSelecionado?.media_consumo_km_litro,
  });

  const handleVeiculoChange = async (veiculoId: number | null) => {
    setForm((prev) => ({ ...prev, veiculo_id: veiculoId }));
    if (!veiculoId) {
      setVeiculoSelecionado(null);
      return;
    }
    try {
      const res = await frotaService.obterVeiculo(veiculoId);
      const veiculo = res.data || null;
      setVeiculoSelecionado(veiculo);
      if (veiculo) {
        setForm((prev) => ({
          ...prev,
          veiculo_id: veiculoId,
          kilometragem_atual: veiculo.kilometragem_atual,
          funcionario_solicitante_id: veiculo.motorista_atual?.id || prev.funcionario_solicitante_id,
          escritorio_id: veiculo.escritorio_id,
        }));
      }
    } catch {
      setVeiculoSelecionado(null);
    }
  };

  const loadStats = async () => {
    try {
      const [totalRes, pendenteRes, aprovadaRes, rejeitadaRes, apuradaRes] = await Promise.all([
        frotaService.listarAbastecimentos({ limit: 1 }),
        frotaService.listarAbastecimentos({ status: 'pendente', limit: 1 }),
        frotaService.listarAbastecimentos({ status: 'aprovada', limit: 1 }),
        frotaService.listarAbastecimentos({ status: 'rejeitada', limit: 1 }),
        frotaService.listarAbastecimentos({ status: 'apurada', limit: 1 }),
      ]);
      setStats({
        total: totalRes.pagination?.total || 0,
        pendente: pendenteRes.pagination?.total || 0,
        aprovada: aprovadaRes.pagination?.total || 0,
        rejeitada: rejeitadaRes.pagination?.total || 0,
        apurada: apuradaRes.pagination?.total || 0,
      });
    } catch {
      /* mantém stats anteriores */
    }
  };

  const buildFiltros = () => ({
    status: status || undefined,
    data_inicio: dateToYmd(dataRange.startDate) || undefined,
    data_fim: dateToYmd(dataRange.endDate) || undefined,
    veiculo_id: filtroVeiculoId ?? undefined,
    funcionario_solicitante_id: filtroSolicitanteId ?? undefined,
    usuario_lancou_id: filtroUsuarioLancouId ?? undefined,
    tipo_combustivel: filtroCombustivel || undefined,
    page,
    limit: 20,
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = await frotaService.listarAbastecimentos(buildFiltros());
      setItems(res.data || []);
      setTotalPages(res.pagination?.pages || 1);
    } catch {
      toast.error('Erro ao carregar requisições');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [status, dataRange, filtroVeiculoId, filtroSolicitanteId, filtroUsuarioLancouId, filtroCombustivel, page]);

  const handleCriar = async () => {
    if (!form.veiculo_id || !form.funcionario_solicitante_id || !form.valor || !form.tipo_combustivel || !form.valor_litro) {
      toast.error('Preencha veículo, solicitante, combustível, valor por litro e valor total');
      return;
    }
    if (form.valor_litro <= 0) {
      toast.error('O valor por litro deve ser maior que zero');
      return;
    }
    try {
      await frotaService.criarAbastecimento({
        veiculo_id: form.veiculo_id,
        kilometragem_atual: form.kilometragem_atual,
        valor: form.valor,
        tipo_combustivel: form.tipo_combustivel,
        valor_litro: form.valor_litro,
        funcionario_solicitante_id: form.funcionario_solicitante_id,
        data_requisicao: form.data_requisicao,
        observacoes: form.observacoes || undefined,
        escritorio_id: form.escritorio_id ?? undefined,
      });
      toast.success('Requisição criada');
      setOpenForm(false);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao criar requisição');
    }
  };

  const handleAprovar = async (id: number, aprovado: boolean) => {
    try {
      await frotaService.aprovarAbastecimento(id, aprovado);
      toast.success(aprovado ? 'Aprovada' : 'Rejeitada');
      load();
      loadStats();
    } catch {
      toast.error('Erro ao processar');
    }
  };

  const abrirApuracao = async () => {
    const mesRef = apuracao.mes_referencia || new Date().toISOString().slice(0, 7);
    setApuracao((p) => ({ ...p, mes_referencia: mesRef }));
    try {
      const res = await frotaService.resumoApuracao(mesRef);
      setResumo(res.data);
      setOpenApuracao(true);
    } catch {
      toast.error('Erro ao carregar resumo');
    }
  };

  const executarApuracao = async () => {
    if (!apuracao.mes_referencia || !apuracao.escritorio_id) {
      toast.error('Informe mês e escritório');
      return;
    }
    try {
      const res = await frotaService.apurarAbastecimentos({
        mes_referencia: apuracao.mes_referencia,
        escritorio_id: apuracao.escritorio_id,
        data_vencimento: apuracao.data_vencimento || undefined,
        observacoes: apuracao.observacoes || undefined,
      });
      toast.success(res.message || 'Apuração realizada! Despesa criada em Contas a Pagar.');
      setOpenApuracao(false);
      load();
      loadStats();
    } catch (err: any) {
      toast.error(err?.message || 'Erro na apuração');
    }
  };

  const statItems: FrotaStatItem[] = [
    { key: 'todos', label: 'Total', value: stats.total, icon: <LocalGasStation />, filterValue: '' },
    { key: 'pendente', label: 'Pendentes', value: stats.pendente, color: STATUS_COLORS.pendente, icon: <HourglassEmpty />, filterValue: 'pendente' },
    { key: 'aprovada', label: 'Aprovadas', value: stats.aprovada, color: STATUS_COLORS.aprovada, icon: <CheckCircle />, filterValue: 'aprovada' },
    { key: 'rejeitada', label: 'Rejeitadas', value: stats.rejeitada, color: STATUS_COLORS.rejeitada, icon: <Cancel />, filterValue: 'rejeitada' },
    { key: 'apurada', label: 'Apuradas', value: stats.apurada, color: STATUS_COLORS.apurada, icon: <Receipt />, filterValue: 'apurada' },
  ];

  return (
    <>
      <FrotaPageLayout
        title="Requisições de Abastecimento"
        subtitle="Solicitações, aprovações e apuração mensal de combustível"
        icon={<LocalGasStation fontSize="large" />}
        iconColor="#4CAF50"
        stats={statItems}
        activeFilter={status}
        onFilterChange={(v) => { setStatus(v); setPage(1); }}
        loading={loading}
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => { load(); loadStats(); }} sx={{ borderRadius: 2 }}>
              Atualizar
            </Button>
            <Button startIcon={<Calculate />} variant="outlined" onClick={abrirApuracao} sx={{ borderRadius: 2 }}>
              Apurar mês
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => { resetFormValores(); setOpenForm(true); }} sx={{ borderRadius: 2, boxShadow: 2 }}>
              Nova requisição
            </Button>
          </>
        }
        filters={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={1.5}>
              <SysDateRangePicker
                label="Data"
                value={dataRange}
                onChange={(range) => { setDataRange(range); setPage(1); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <VeiculoSelect
                label="Veículo"
                value={filtroVeiculoId}
                onChange={(v) => { setFiltroVeiculoId(v); setPage(1); }}
                apenasAtivos={false}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FuncionariosSelect
                label="Quem pediu"
                placeholder="Todos"
                value={filtroSolicitanteId}
                onChange={(v) => { setFiltroSolicitanteId(v); setPage(1); }}
                size="small"
                allowClear
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsuariosSelect
                label="Quem lançou"
                placeholder="Todos"
                value={filtroUsuarioLancouId}
                onChange={(v) => { setFiltroUsuarioLancouId(v); setPage(1); }}
                size="small"
                allowClear
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.2}>
              <FormControl fullWidth size="small">
                <InputLabel>Combustível</InputLabel>
                <Select
                  value={filtroCombustivel}
                  label="Combustível"
                  onChange={(e) => { setFiltroCombustivel(e.target.value as TipoCombustivel | ''); setPage(1); }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {TIPOS_COMBUSTIVEL.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ borderRadius: 2 }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="aprovada">Aprovada</MenuItem>
                  <MenuItem value="rejeitada">Rejeitada</MenuItem>
                  <MenuItem value="apurada">Apurada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Veículo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Quem pediu</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Quem lançou</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>KM</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Combustível</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>R$/L</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Litros</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Valor total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.data_requisicao}</TableCell>
                  <TableCell>{r.veiculo?.placa}</TableCell>
                  <TableCell>{r.funcionario_solicitante?.nome}</TableCell>
                  <TableCell>{r.usuario_lancou?.nome}</TableCell>
                  <TableCell>{r.kilometragem_atual?.toLocaleString('pt-BR')}</TableCell>
                  <TableCell>{frotaService.tipoCombustivelLabel(r.tipo_combustivel)}</TableCell>
                  <TableCell>{frotaService.formatCurrency(Number(r.valor_litro))}</TableCell>
                  <TableCell>{frotaService.formatQuantidadeLitros(Number(r.quantidade_litros))}</TableCell>
                  <TableCell>{frotaService.formatCurrency(Number(r.valor))}</TableCell>
                  <TableCell>
                    <Chip size="small" label={frotaService.statusAbastecimentoLabel(r.status)} color={statusColors[r.status]} />
                  </TableCell>
                  <TableCell align="right">
                    {r.status === 'pendente' && (
                      <>
                        <Tooltip title="Aprovar"><IconButton size="small" color="success" onClick={() => handleAprovar(r.id, true)}><Check /></IconButton></Tooltip>
                        <Tooltip title="Rejeitar"><IconButton size="small" color="error" onClick={() => handleAprovar(r.id, false)}><Close /></IconButton></Tooltip>
                      </>
                    )}
                    {r.despesa_id && <Chip size="small" label={`Despesa #${r.despesa_id}`} variant="outlined" />}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={11}><Alert severity="info">Nenhuma requisição</Alert></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </FrotaPageLayout>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova requisição de abastecimento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><VeiculoSelect value={form.veiculo_id} onChange={handleVeiculoChange} required /></Grid>
            <Grid item xs={12}><FuncionariosSelect label="Quem pediu (solicitante)" value={form.funcionario_solicitante_id} onChange={(v) => setForm({ ...form, funcionario_solicitante_id: v })} required /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de combustível</InputLabel>
                <Select size="small" value={form.tipo_combustivel} label="Tipo de combustível"
                  onChange={(e) => setForm({ ...form, tipo_combustivel: e.target.value as TipoCombustivel })}>
                  {TIPOS_COMBUSTIVEL.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth type="text" label="Valor por litro (R$)"
                value={valorLitroFormatado}
                onChange={(e) => {
                  const fmt = formatarValorOnInput(e.target.value);
                  const num = parsearValorFormatado(fmt);
                  setValorLitroFormatado(fmt);
                  setForm((prev) => ({ ...prev, valor_litro: num }));
                }}
              />
            </Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="number" label="KM atual" value={form.kilometragem_atual} onChange={(e) => setForm({ ...form, kilometragem_atual: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth type="text" label="Valor total (R$)"
                value={valorFormatado}
                onChange={(e) => {
                  const fmt = formatarValorOnInput(e.target.value);
                  const num = parsearValorFormatado(fmt);
                  setValorFormatado(fmt);
                  setForm((prev) => ({ ...prev, valor: num }));
                }}
              />
            </Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="date" label="Data" InputLabelProps={{ shrink: true }} value={form.data_requisicao} onChange={(e) => setForm({ ...form, data_requisicao: e.target.value })} /></Grid>
            <Grid item xs={6}><EscritorioSelect value={form.escritorio_id} onChange={(v) => setForm({ ...form, escritorio_id: v as number })} /></Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 0.5 }}>
                <Typography variant="subtitle2" gutterBottom>Estatísticas estimadas</Typography>
                <Typography variant="body2">
                  Qtde de Litros:{' '}
                  <strong>{quantidadeLitros > 0 ? `${quantidadeLitros.toLocaleString('pt-BR')} L` : 'Não informada no cadastro'}</strong>
                </Typography>
                <Typography variant="body2">
                  Média de consumo do veículo:{' '}
                  <strong>{estatisticas.media > 0 ? `${estatisticas.media.toLocaleString('pt-BR')} Km/L` : 'Não informada no cadastro'}</strong>
                </Typography>
                <Typography variant="body2">
                  Km estimados com este abastecimento:{' '}
                  <strong>{estatisticas.kmComAbastecimento > 0 ? `${estatisticas.kmComAbastecimento.toLocaleString('pt-BR')} km` : '-'}</strong>
                </Typography>
                <Typography variant="body2">
                  Próxima kilometragem estimada:{' '}
                  <strong>{estatisticas.proximaKilometragem > 0 ? `${estatisticas.proximaKilometragem} km` : '-'}</strong>
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCriar}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openApuracao} onClose={() => setOpenApuracao(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apurar abastecimentos do mês</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Consolida todas as requisições aprovadas do mês e gera uma despesa em Contas a Pagar.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth type="month" label="Mês referência" InputLabelProps={{ shrink: true }}
                value={apuracao.mes_referencia} onChange={(e) => setApuracao({ ...apuracao, mes_referencia: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <EscritorioSelect value={apuracao.escritorio_id} onChange={(v) => setApuracao({ ...apuracao, escritorio_id: v as number })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="date" label="Data vencimento da despesa" InputLabelProps={{ shrink: true }}
                value={apuracao.data_vencimento} onChange={(e) => setApuracao({ ...apuracao, data_vencimento: e.target.value })} />
            </Grid>
            {resumo && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {resumo.quantidade} requisição(ões) aprovada(s) — Total: {frotaService.formatCurrency(resumo.valor_total)}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApuracao(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={executarApuracao}>Gerar despesa</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AbastecimentosPage;
